/**
 * @lines98/client/components/info-popover — shared `?`/`!`-trigger + native
 * `<dialog>` popover for descriptive copy (mobile only) и cross-platform
 * informational countdown (e.g., share-link availability).
 *
 * Spec: `docs/specs/ui-components.md` §1 (AC-1..AC-15) + §1.13 (variant `?`/`!`
 * + dynamic body content, AC-13.1..AC-13.6 — brief-33 §B.1 owner-decision).
 *
 * Skill mandate: `.claude/skills/responsive-ui-design/SKILL.md` §0.E.
 *
 * Two orthogonal axes of configuration:
 *
 *   (a) trigger variant — `?` (default, touch-only) или `!` (cross-platform).
 *       - `?`: descriptive copy за иконкой; на ПК скрыт через CSS media
 *         query (`(hover:hover) and (pointer:fine)`), на touch
 *         (`(pointer:coarse)`) виден. T-7.2.2 (2026-05-26): device-type
 *         detection вместо прежних width-breakpoints.
 *         Surface-потребитель сам рендерит inline-копию descriptive copy для
 *         ПК через media query (`display: block`).
 *       - `!`: важное уведомление-ожидание (countdown / time-related). Visible
 *         на ВСЕХ viewport'ах. НЕ hover-on-parent (намеренно по owner-decision
 *         brief-33 §B.1 — «кнопка действует, иконка информирует»).
 *
 *   (b) body content — static (`bodyText`) или dynamic (`bodyDynamic`
 *       callback, обновляется каждые ~1s в open-state).
 *
 * Mechanics — все из коробки native `<dialog>` (Chrome 37+ / Safari 15.4+
 * / Firefox 98+):
 *   - top-layer (выше любого z-index родителя).
 *   - ::backdrop pseudo-element (затемнитель фона).
 *   - focus-trap (`inert` на everything outside).
 *   - Esc-handling.
 *   - role="dialog" accessibility.
 *
 * Closing: tap on backdrop / Esc / `×` button — все три механизма (AC-5/6/7).
 *
 * Animation: `opacity 0→1 + transform: scale(0.95→1)` 150ms ease-out на open;
 * 120ms ease-in на close (AC-10). GPU-only (opacity + transform не вызывают
 * layout reflow). `prefers-reduced-motion: reduce` отключает (AC-11).
 *
 * One-popover-at-a-time discipline (AC-12): открытие нового триггера авто-
 * закрывает любой ранее открытый popover.
 *
 * Cross-references:
 *   - `docs/specs/ui-components.md` §1.1..§1.13.
 *   - `docs/specs/ui-surfaces-t-7.3.md` §5.1..§5.4 (потребители).
 *   - `.claude/skills/responsive-ui-design/SKILL.md` §0.B + §0.E.
 *   - `packages/client/src/components/info-popover.css` (стили).
 */

import './info-popover.css';

import { positionPopoverNearTrigger } from '../lib/anchored-popover.js';
import { t } from '../lib/i18n/index.js';
import { createIconButton, ICON_CLOSE } from './icon-button.js';

// =============================================================================
// Types
// =============================================================================

/** Trigger icon variant — see file header. */
export type InfoPopoverVariant = '?' | '!';

export interface InfoPopoverOptions {
  /**
   * Title rendered inside popover header (above body). Often duplicates surface
   * title или его близкий вариант — surface-потребитель сам решает.
   */
  readonly title: string;
  /**
   * Static body text. Mutually exclusive с `bodyDynamic`. Plain text — компонент
   * не интерпретирует HTML (для accessibility и анти-XSS дисциплины).
   */
  readonly bodyText?: string;
  /**
   * Dynamic body callback. Called каждый tick (~1s) в open-state to refresh
   * the body content. Return value заменяет `.info-popover-body` innerText.
   *
   * Использовать только для time-driven content (countdown'ы / time-to-ready).
   * Static content — через `bodyText` (cheaper, no interval).
   */
  readonly bodyDynamic?: () => string;
  /**
   * Tick interval для `bodyDynamic` в ms. Defaults 1000.
   */
  readonly bodyDynamicIntervalMs?: number;
  /**
   * DOM element where the `?`/`!`-trigger button мontируется (appended as last
   * child). Surface-потребитель размещает trigger-host рядом с заголовком или
   * внутри action-кнопки (для `!`-variant).
   */
  readonly triggerHost: HTMLElement;
  /**
   * Trigger variant — see file header.
   */
  readonly variant?: InfoPopoverVariant;
  /**
   * Unique ID for popover element. Used in `aria-controls` / `aria-describedby`.
   * Auto-generated if absent.
   */
  readonly id?: string;
}

export interface InfoPopoverHandle {
  /**
   * Programmatically open the popover. Same effect as user clicking the
   * trigger.
   */
  open(): void;
  /**
   * Programmatically close the popover.
   */
  close(): void;
  /**
   * Unmount: closes any open popover, removes trigger + dialog from DOM,
   * detaches all listeners and intervals.
   */
  unmount(): void;
}

// =============================================================================
// Global registry — enforces "one open popover at a time" (AC-12)
// =============================================================================

/**
 * Track all currently-open popovers. When a new one opens, the registry closes
 * any others. We register both `open` action и a sentinel close fn, keeping
 * the surface-API simple.
 */
const openRegistry = new Set<() => void>();

function closeAllOpen(except?: () => void): void {
  for (const closeFn of openRegistry) {
    if (closeFn !== except) {
      closeFn();
    }
  }
}

// =============================================================================
// ID generator
// =============================================================================

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `info-popover-${idCounter}`;
}

// =============================================================================
// Mount
// =============================================================================

/**
 * Mount an `InfoPopover` instance.
 *
 * Returns а handle с `open()` / `close()` / `unmount()`. Surface-потребитель
 * вызывает `unmount()` при unmount-е своего экрана (типично в `ScreenHandle
 * .unmount()`).
 */
export function mountInfoPopover(opts: InfoPopoverOptions): InfoPopoverHandle {
  const variant: InfoPopoverVariant = opts.variant ?? '?';
  const id = opts.id ?? nextId();
  const intervalMs = opts.bodyDynamicIntervalMs ?? 1000;

  // ---- Trigger button ----
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = `info-popover-trigger info-popover-trigger--${variant === '?' ? 'q' : 'b'}`;
  trigger.setAttribute('aria-label', t('game.infoPopover.more'));
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', id);
  trigger.textContent = variant;
  opts.triggerHost.appendChild(trigger);

  // ---- Dialog ----
  const dialog = document.createElement('dialog');
  dialog.className = 'info-popover-dialog';
  dialog.id = id;

  const header = document.createElement('header');
  header.className = 'info-popover-header';

  const titleEl = document.createElement('h2');
  titleEl.className = 'info-popover-title';
  titleEl.textContent = opts.title;
  header.appendChild(titleEl);

  // × — примитив icon-button (ghost, SVG-крестик по центру, тактильные 44px).
  const closeBtn = createIconButton({
    glyph: ICON_CLOSE,
    label: t('game.infoPopover.close'),
    variant: 'ghost',
    className: 'info-popover-close',
  }).element;
  header.appendChild(closeBtn);

  dialog.appendChild(header);

  const bodyEl = document.createElement('div');
  bodyEl.className = 'info-popover-body';
  const bodyId = `${id}-body`;
  bodyEl.id = bodyId;
  dialog.setAttribute('aria-describedby', bodyId);

  if (opts.bodyDynamic) {
    bodyEl.textContent = opts.bodyDynamic();
  } else {
    bodyEl.textContent = opts.bodyText ?? '';
  }
  dialog.appendChild(bodyEl);

  document.body.appendChild(dialog);

  // ---- Internal state ----
  let intervalId: number | null = null;
  let unmounted = false;

  const stopInterval = (): void => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  const closeFn = (): void => {
    if (unmounted) return;
    if (dialog.open) {
      // Trigger close animation: add `--closing` class for the duration of
      // the leave transition (CSS handles opacity + transform), then call
      // native `close()`. We don't block on the transition — native
      // `dialog.close()` is sync, and our CSS uses а brief opacity drop;
      // browsers de-cache focus restoration to next paint anyway.
      dialog.classList.add('info-popover-dialog--closing');
      // Use rAF so the closing class lands before the close-transition;
      // browsers paint the transition then call close().
      window.requestAnimationFrame(() => {
        dialog.close();
        dialog.classList.remove('info-popover-dialog--closing');
      });
    }
    stopInterval();
    trigger.setAttribute('aria-expanded', 'false');
    openRegistry.delete(closeFn);
  };

  /**
   * Поставить попап РЯДОМ с кнопкой-триггером (owner-directive 2026-05-29: «текст
   * рядом с ней, чтобы было понятно, что эта кнопка вызвала»), а НЕ по центру экрана.
   * Делегирует ЕДИНОМУ анкер-помощнику `positionPopoverNearTrigger` (R-1 — один источник
   * анкер-механики, общий с деталью ачивки; дубля `getBoundingClientRect`-логики нет).
   * `?`/`!`-инфо-копи раскрывается ПОД кнопкой (`prefer:'below'` — историческое поведение
   * info-popover), флип вверх, если снизу не хватает. Хвостика нет (инфо-вариант).
   */
  function positionNearTrigger(): void {
    positionPopoverNearTrigger(dialog, trigger, { prefer: 'below' });
  }

  const openFn = (): void => {
    if (unmounted) return;
    // AC-12: close any other open popovers first.
    closeAllOpen(closeFn);

    // Refresh body once before opening if dynamic, so the initial render is
    // current (e.g., countdown not stale).
    if (opts.bodyDynamic) {
      bodyEl.textContent = opts.bodyDynamic();
    }

    dialog.showModal();
    positionNearTrigger();
    trigger.setAttribute('aria-expanded', 'true');
    openRegistry.add(closeFn);

    if (opts.bodyDynamic) {
      // Restart interval — guard против overlapping intervals if open re-fired.
      stopInterval();
      intervalId = window.setInterval(() => {
        // Defensive: if dialog was closed externally, stop.
        if (!dialog.open) {
          stopInterval();
          return;
        }
        if (opts.bodyDynamic) {
          bodyEl.textContent = opts.bodyDynamic();
        }
      }, intervalMs);
    }
  };

  // ---- Listeners ----

  // Trigger click — open. `stopPropagation` only matters for `!`-variant
  // when nested inside a parent button (e.g., `.result-action--share`):
  // AC-13.3 requires the parent button NOT activate when the `!`-icon is
  // tapped. Cheap to apply universally — `?`-trigger is typically NOT inside
  // a button, but the stopProp doesn't hurt.
  const onTriggerClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    openFn();
  };
  trigger.addEventListener('click', onTriggerClick);

  // Close button.
  const onCloseClick = (e: Event): void => {
    e.stopPropagation();
    e.preventDefault();
    closeFn();
  };
  closeBtn.addEventListener('click', onCloseClick);

  // Backdrop click — closes only when the click landed on the dialog itself
  // (not children). `e.target === dialog` is true when the click hits the
  // ::backdrop / padding area (AC-5 + AC-8).
  const onDialogClick = (e: MouseEvent): void => {
    if (e.target === dialog) {
      closeFn();
    }
  };
  dialog.addEventListener('click', onDialogClick);

  // Esc handling — native `<dialog>` fires `cancel` (Esc) AND `close` events.
  // We listen to `close` to keep our aria-expanded + interval state in sync
  // when native dispatched the close. Don't call our own `closeFn` recursively
  // — only do the cleanup that's safe post-close.
  const onDialogClose = (): void => {
    if (unmounted) return;
    stopInterval();
    trigger.setAttribute('aria-expanded', 'false');
    openRegistry.delete(closeFn);
  };
  dialog.addEventListener('close', onDialogClose);

  // ---- Handle ----

  return {
    open: openFn,
    close: closeFn,
    unmount(): void {
      if (unmounted) return;
      unmounted = true;
      stopInterval();
      openRegistry.delete(closeFn);

      // Detach listeners.
      trigger.removeEventListener('click', onTriggerClick);
      closeBtn.removeEventListener('click', onCloseClick);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('close', onDialogClose);

      // Close native dialog before removal to release top-layer.
      if (dialog.open) {
        dialog.close();
      }

      // Remove from DOM. Defensive: parentNode may be null if surface already
      // unmounted the trigger-host (e.g., via host.innerHTML = '').
      if (trigger.parentNode) trigger.parentNode.removeChild(trigger);
      if (dialog.parentNode) dialog.parentNode.removeChild(dialog);
    },
  };
}
